import * as R from 'ramda';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { PaginationButton } from './PaginationButton';

interface PaginationProps {
  /** Total number of pages. */
  total: number;

  /** Current page number. */
  page: number;

  /** Callback fired when the page is changed. */
  onPageChange: (page: number) => void;

  /** Optional label for the pagination. */
  label?: string;

  /** Accessible label for this pagination landmark. */
  ariaLabel?: string;

  /** If `true`, the go-to page input will be displayed. */
  goTo?: boolean;
}

export function Pagination({
  total,
  page,
  onPageChange,
  label,
  ariaLabel,
  goTo = true
}: PaginationProps) {
  const [goToPage, setGoToPage] = useState<number | ''>('');
  const [goToValidationMessage, setGoToValidationMessage] = useState('');
  const pageButtonRefs = useRef<Map<number, HTMLButtonElement | null>>(
    new Map()
  );
  const goToInputId = useId();
  const goToValidationStatusId = useId();
  const lastInteractionRef = useRef<{
    type: 'page' | 'prev' | 'next' | 'go-to';
    requestedPage: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!lastInteractionRef.current) {
      return;
    }

    const { type, requestedPage } = lastInteractionRef.current;
    lastInteractionRef.current = null;

    if (type === 'page') {
      const clickedButtonStillMounted =
        pageButtonRefs.current.get(requestedPage) ?? null;

      if (clickedButtonStillMounted) {
        return;
      }
    }

    pageButtonRefs.current.get(page)?.focus();
  }, [page, total]);

  function handlePageChange(
    nextPage: number,
    type: 'page' | 'prev' | 'next' | 'go-to'
  ) {
    lastInteractionRef.current = { type, requestedPage: nextPage };
    onPageChange(nextPage);
  }

  function setPageButtonRef(
    pageNumber: number,
    element: HTMLButtonElement | null
  ) {
    pageButtonRefs.current.set(pageNumber, element);
  }

  const handlePrev = () => handlePageChange(page - 1, 'prev');
  const handleNext = () => handlePageChange(page + 1, 'next');
  const firstPage = 1;
  const lastPage = total;
  const navAriaLabel = ariaLabel || i18n.ft(messages.paginationAriaLabel);
  const controlLabelContext = ariaLabel
    ? i18n.ft(messages.inContext, { context: ariaLabel })
    : '';

  function getPageLabel(pageNumber: number) {
    return i18n.ft(messages.pageAriaLabel, {
      page: pageNumber,
      total,
      context: controlLabelContext
    });
  }

  function calculatePages() {
    if (total <= 7) {
      return R.range(2, total);
    }

    if (page <= 4) {
      return R.range(2, 6);
    }

    if (page >= total - 3) {
      return R.range(total - 4, total);
    }

    return R.range(page - 1, page + 2);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    handleGoToPage();
  }

  function handleGoToPage() {
    if (typeof goToPage !== 'number' || isNaN(goToPage)) {
      setGoToValidationMessage(
        i18n.ft(messages.invalidPageNumber, {
          total
        })
      );
      setGoToPage('');
      return;
    }

    if (goToPage < 1 || goToPage > total) {
      setGoToValidationMessage(
        i18n.ft(messages.invalidPageNumber, {
          total
        })
      );
      setGoToPage('');
      return;
    }

    setGoToValidationMessage('');
    handlePageChange(goToPage, 'go-to');
    setGoToPage('');
  }

  const pages = calculatePages();

  return (
    <nav className="flex gap-1" aria-label={navAriaLabel}>
      {label && <span className="flex items-start mt-1 mr-1">{label}</span>}

      <PaginationButton
        onClick={handlePrev}
        disabled={page <= 1}
        aria-label={i18n.ft(messages.previousPageAriaLabel, {
          context: controlLabelContext
        })}
      >
        <i className="fa-solid fa-chevron-left" />
      </PaginationButton>

      {total >= 1 && (
        <PaginationButton
          ref={element => setPageButtonRef(firstPage, element)}
          active={page === firstPage}
          onClick={() => handlePageChange(firstPage, 'page')}
          aria-label={getPageLabel(firstPage)}
          aria-current={page === firstPage ? 'page' : undefined}
        >
          {firstPage}
        </PaginationButton>
      )}

      {total > 7 && page > 4 && (
        <PaginationButton disabled aria-hidden="true">
          <i className="fa-solid fa-ellipsis" />
        </PaginationButton>
      )}

      {pages.map(pageNumber => (
        <PaginationButton
          ref={element => setPageButtonRef(pageNumber, element)}
          key={pageNumber}
          active={page === pageNumber}
          onClick={() => handlePageChange(pageNumber, 'page')}
          aria-label={getPageLabel(pageNumber)}
          aria-current={page === pageNumber ? 'page' : undefined}
        >
          {pageNumber}
        </PaginationButton>
      ))}

      {total > 7 && page < total - 3 && (
        <PaginationButton disabled aria-hidden="true">
          <i className="fa-solid fa-ellipsis" />
        </PaginationButton>
      )}

      {total > 1 && (
        <PaginationButton
          ref={element => setPageButtonRef(lastPage, element)}
          active={page === lastPage}
          onClick={() => handlePageChange(lastPage, 'page')}
          aria-label={getPageLabel(lastPage)}
          aria-current={page === lastPage ? 'page' : undefined}
        >
          {lastPage}
        </PaginationButton>
      )}

      <PaginationButton
        onClick={handleNext}
        disabled={page >= total}
        aria-label={i18n.ft(messages.nextPageAriaLabel, {
          context: controlLabelContext
        })}
      >
        <i className="fa-solid fa-chevron-right" />
      </PaginationButton>

      {goTo && (
        <div className="ml-3 flex gap-1 items-start">
          <label htmlFor={goToInputId} className="mt-1">
            {i18n.ft(messages.pageInputLabel)}
          </label>
          <div className="flex flex-col">
            <input
              id={goToInputId}
              type="number"
              min={1}
              max={total}
              inputMode="numeric"
              aria-invalid={goToValidationMessage ? true : undefined}
              aria-describedby={
                goToValidationMessage ? goToValidationStatusId : undefined
              }
              value={goToPage}
              onBlur={handleGoToPage}
              onKeyDown={handleKeyDown}
              onChange={e => {
                setGoToValidationMessage('');
                setGoToPage(
                  e.target.value === '' ? '' : Number(e.target.value)
                );
              }}
              className="font-sans h-8 w-16 px-2 border border-[#889EBB] rounded"
            />
            {goToValidationMessage && (
              <p
                id={goToValidationStatusId}
                role="alert"
                className="mt-1 text-ds-tiny text-ds-tomato-10"
              >
                {goToValidationMessage}
              </p>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
